/**
 * RoutePreviewMap - Compact Google Maps preview for route cards
 *
 * Shows numbered markers for all stops and a driving route polyline.
 * Designed for the RoutesPage overview - lightweight version of the office-app RouteMap.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
} from '@react-google-maps/api';
import type { GeplandeRitWithDetails } from '../../lib/salesforce/types';

// Havelte depot - start/end point for all routes
const DEPOT = { lat: 52.7582089, lng: 6.2362659 };

// Google Directions API supports max 25 points (origin + destination + 23 waypoints)
const MAX_WAYPOINTS = 23;

const mapContainerStyle = { width: '100%', height: '100%' };

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

interface RoutePreviewMapProps {
  stops: GeplandeRitWithDetails[];
  height?: number;
}

export function RoutePreviewMap({ stops, height = 200 }: RoutePreviewMapProps) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  });

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [directionsResults, setDirectionsResults] = useState<google.maps.DirectionsResult[]>([]);

  // Filter stops with valid coordinates, sorted by sequence
  const stopsWithCoords = useMemo(() =>
    stops
      .filter(s => s.latitude != null && s.longitude != null)
      .sort((a, b) => (a.Route_Sequence__c ?? 0) - (b.Route_Sequence__c ?? 0)),
    [stops]
  );

  // Stable key to avoid re-fetching directions when nothing changed
  const stopsKey = useMemo(() =>
    stopsWithCoords.map(s => s.Id).join(','),
    [stopsWithCoords]
  );

  // Fit bounds to all markers when map loads or stops change
  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapRef(map);
    if (stopsWithCoords.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(DEPOT);
    stopsWithCoords.forEach(s => bounds.extend({ lat: s.latitude!, lng: s.longitude! }));
    map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
  }, [stopsWithCoords]);

  // Fetch driving directions
  useEffect(() => {
    if (!isLoaded || !mapRef || stopsWithCoords.length === 0) return;

    const directionsService = new google.maps.DirectionsService();
    const allPoints = [
      DEPOT,
      ...stopsWithCoords.map(s => ({ lat: s.latitude!, lng: s.longitude! })),
      DEPOT,
    ];

    // Split into chunks if needed (Google limit: 25 points per request)
    const chunkSize = MAX_WAYPOINTS + 2; // +2 for origin + destination
    const chunks: google.maps.LatLngLiteral[][] = [];
    for (let i = 0; i < allPoints.length - 1; i += chunkSize - 1) {
      const chunk = allPoints.slice(i, Math.min(i + chunkSize, allPoints.length));
      if (chunk.length >= 2) chunks.push(chunk);
    }

    const results: google.maps.DirectionsResult[] = [];

    const processChunk = (idx: number) => {
      if (idx >= chunks.length) {
        setDirectionsResults(results);
        return;
      }

      const chunk = chunks[idx]!;
      const origin = chunk[0]!;
      const destination = chunk[chunk.length - 1]!;
      const waypoints = chunk.slice(1, -1).map(p => ({
        location: new google.maps.LatLng(p.lat, p.lng),
        stopover: true,
      }));

      directionsService.route(
        {
          origin,
          destination,
          waypoints,
          optimizeWaypoints: false,
          travelMode: google.maps.TravelMode.DRIVING,
          region: 'NL',
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            results.push(result);
            setTimeout(() => processChunk(idx + 1), 100);
          } else {
            console.warn('[RoutePreviewMap] Directions failed:', status);
            // Still show markers even if directions fail
            setDirectionsResults(results);
          }
        }
      );
    };

    processChunk(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapRef, stopsKey]);

  if (!isLoaded) {
    return (
      <div
        className="flex items-center justify-center bg-wb-gedempt border-wb border-wb-rand rounded-wb-lg"
        style={{ height }}
      >
        <div className="font-mono text-sm text-wb-inkt2">Kaart laden…</div>
      </div>
    );
  }

  if (stopsWithCoords.length === 0) {
    return null;
  }

  return (
    <div className="rounded-wb-lg border-wb border-wb-rand overflow-hidden" style={{ height }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        options={mapOptions}
        onLoad={onMapLoad}
        center={DEPOT}
        zoom={8}
      >
        {/* Depot marker */}
        <Marker
          position={DEPOT}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: '#5bbdb6',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
          title="Havelte (depot)"
        />

        {/* Stop markers with sequence numbers */}
        {stopsWithCoords.map((stop, index) => (
          <Marker
            key={stop.Id}
            position={{ lat: stop.latitude!, lng: stop.longitude! }}
            label={{
              text: String(index + 1),
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: stop.Status__c === 'Verwerkt' ? '#55707a' : '#12333d',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
            title={`${index + 1}. ${stop.customerName || 'Stop'}`}
          />
        ))}

        {/* Driving route polylines */}
        {directionsResults.map((result, idx) => (
          <DirectionsRenderer
            key={idx}
            directions={result}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#194558',
                strokeWeight: 3,
                strokeOpacity: 0.7,
              },
            }}
          />
        ))}
      </GoogleMap>
    </div>
  );
}
